import { Filter, FilterType } from '@dfl/mui-admin-layout';
import { createdATFilter } from 'modules/common/constants';
import { userAdminFilter } from 'modules/security/users/constants/user-filters';
import { INCIDENCE_STATUS_ENUM } from './incidence-status';
import { CausesIncidenceService } from 'modules/sales/settings/causes-incidence/services';
import {
  CAUSES_INCIDENCES_LIST_KEY,
  CAUSES_INCIDENCES_CHILDREN_KEY,
} from 'modules/sales/settings/causes-incidence/constants';
import { ORDER_REFERENCE_TYPE } from 'modules/sales/common/constants/order.enum';
import { sanitizeTextFilter } from 'utils/search.utils';
import { TermFilter } from '@dofleini/query-builder';
import { DISTRIBUTION_CENTERS_LIST_KEY } from 'modules/inventory/distribution-centers/constants';
import { DistributionCentersService } from 'modules/inventory/distribution-centers/services';
import { transformWhitObjectId } from 'modules/common/constants/object-id';
import { UserAdminService } from 'modules/security/users/services';

export const incidenceStatusFilter: Filter = {
  filter: 'common:status',
  type: FilterType.FIXED_LIST,
  translate: true,
  key: 'status',
  field: 'status',
  options: Object.keys(INCIDENCE_STATUS_ENUM).map((key) => ({
    value: key,
    translate: true,
    label: `incidence:status.${key}`,
  })),
};

export const parentCauseIncidenceFilter = {
  filter: 'incidence:fields.parentCause',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'parentCause',
  labelKey: 'name',
  field: 'cause._id',
  fetchFunc: CausesIncidenceService.searchParents,
  fetchOption: { size: 200 },
  queryKey: CAUSES_INCIDENCES_LIST_KEY,
  transform: (value: any) => transformWhitObjectId(value, 'cause._id'),
};

export const subCauseIncidenceFilter = {
  filter: 'incidence:fields.subCause',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'subCause',
  labelKey: 'name',
  field: 'subCause._id',
  fetchFunc: CausesIncidenceService.searchChildren,
  fetchOption: { size: 200 },
  queryKey: CAUSES_INCIDENCES_CHILDREN_KEY,
  transform: (value: any) => transformWhitObjectId(value, 'subCause._id'),
};

const codeFilter: Filter = {
  filter: 'incidence:fields.code',
  translate: true,
  type: FilterType.TEXT,
  key: 'code',
  field: 'code',
  transform: (value: any) => {
    const queryText = sanitizeTextFilter(value);
    if (!queryText) return undefined;
    return new TermFilter({
      field: 'code',
      value: {
        $regex: queryText,
        $options: 'i',
      },
    });
  },
};

const orderCodeFilter: Filter = {
  filter: 'incidence:fields.orderCode',
  translate: true,
  type: FilterType.TEXT,
  key: 'orderCode',
  field: 'orderReference.code',
  transform: (value: any) => {
    const queryText = sanitizeTextFilter(value);
    if (!queryText) return undefined;
    return new TermFilter({
      field: 'orderReference.code',
      value: {
        $regex: queryText,
        $options: 'i',
      },
    });
  },
};

export const incidenceTypeFilter: Filter = {
  filter: 'incidence:fields.type',
  type: FilterType.FIXED_LIST,
  translate: true,
  key: 'type',
  field: 'referenceType',
  options: Object.keys(ORDER_REFERENCE_TYPE).map((key) => ({
    value: key,
    translate: true,
    label: `incidence:type.${key}`,
  })),
};

export const incidenceDistributionCenterFilter: Filter = {
  filter: 'distributionCenters:name',
  translate: true,
  key: 'dc',
  field: 'orderReference.distributionCenter._id',
  queryKey: DISTRIBUTION_CENTERS_LIST_KEY,
  type: FilterType.DYNAMIC_LIST,
  fetchOption: { size: 200 },
  fetchFunc: DistributionCentersService.searchClean,
  transform: (value) => transformWhitObjectId(value, 'orderReference.distributionCenter._id'),
};

const assignedFilter: Filter = userAdminFilter(
  'responsible._id',
  'incidence:fields.assignedTo',
  'assigned',
  UserAdminService.searchRestrictedAdmin,
);

const createdByFilter: Filter = userAdminFilter('createdBy._id', 'incidence:fields.createdBy', 'created');

export const incidenceFilters = [
  incidenceTypeFilter,
  codeFilter,
  parentCauseIncidenceFilter,
  subCauseIncidenceFilter,
  orderCodeFilter,
  incidenceDistributionCenterFilter,
  incidenceStatusFilter,
  assignedFilter,
  createdByFilter,
  createdATFilter,
];
